import { useCallback } from 'react'
import { useMutation as useConnectMutation } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'

import { Album_AlbumAction } from '@jotta/grpc-connect-openapi/album'
import { patchAlbum as patchAlbumDescriptor } from '@jotta/grpc-connect-openapi/albumQuery'
import { showToast } from '@jotta/ui/ToastProvider'

import { getAlbumQuery, invalidateAlbumQueries } from '../../../api/useAlbum'

export function useAlbumTitleAction({
  albumIdOrShareId,
  enabled = true,
}: {
  albumIdOrShareId: string
  enabled?: boolean
}) {
  const { data: album } = getAlbumQuery({
    variables: {
      albumIdOrShareId,
    },
    enabled: enabled && Boolean(albumIdOrShareId),
    select: data => data.album,
  })
  const albumActions = album?.actions ?? []
  const albumId = album?.id
  const canChangeTitle = albumActions.includes(Album_AlbumAction.CHANGE_TITLE)
  const mutation = useConnectMutation(patchAlbumDescriptor, {
    onSuccess: async () => {
      if (!albumId) {
        return
      }

      await invalidateAlbumQueries(albumId)

      showToast({
        message: t`Album title updated.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
    onError: () => {
      showToast({
        message: t`Cannot update title. Please try again later.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
  })

  const changeTitle = useCallback(
    async (nextTitle: string) => {
      if (!canChangeTitle || !albumId) {
        return
      }

      await mutation.mutateAsync({
        albumId,
        newAlbumTitle: nextTitle,
      })
    },
    [albumId, canChangeTitle, mutation],
  )

  return {
    canChangeTitle,
    changeTitle,
    isPending: mutation.isPending,
  }
}
